import { AccountManager } from '../authConfig';
import { useAuthStore } from './authStore';
import { PERMISSION } from "../authConfig";
class MSALStoreAdapter {
    constructor() {
        this.accountManager = null;
        this.isInitializing = false;
    }
    /**
     * Initialize MSAL with Office context and update store
     */
    async initialize(loginHint) {
        var _a, _b, _c, _d;
        if (this.isInitializing) {
            console.log('MSAL initialization already in progress...');
            return;
        }
        this.isInitializing = true;
        const store = useAuthStore.getState();
        try {
            store.setAuthenticating(true);
            store.setAuthError(undefined);
            // Get login hint from Office context if not provided
            let effectiveLoginHint = loginHint;
            if (!effectiveLoginHint && typeof Office !== 'undefined') {
                const accountType = (_c = (_b = (_a = Office.context) === null || _a === void 0 ? void 0 : _a.mailbox) === null || _b === void 0 ? void 0 : _b.userProfile) === null || _c === void 0 ? void 0 : _c.accountType;
                effectiveLoginHint =
                    (accountType === "office365" || accountType === "outlookCom")
                        ? Office.context.mailbox.userProfile.emailAddress
                        : "";
            }
            if (effectiveLoginHint) {
                store.setLoginHint(effectiveLoginHint);
            }
            // Initialize AccountManager
            this.accountManager = new AccountManager();
            await this.accountManager.initialize(effectiveLoginHint || '');
            console.log('MSAL AccountManager initialized successfully');
            // Get user identity and tokens
            const userAccount = await this.accountManager.ssoGetUserIdentity(PERMISSION);
            console.log('User account retrieved:', userAccount);
            // Extract and validate tokens
            const tokens = {
                accessToken: userAccount.accessToken,
                expiresAt: ((_d = userAccount.expiresOn) === null || _d === void 0 ? void 0 : _d.getTime()) || (Date.now() + 3600000), // 1 hour default
                scopes: userAccount.scopes || ['user.read'],
                idToken: userAccount.idToken,
                tokenType: 'Bearer'
            };
            // Extract user information
            const idTokenClaims = userAccount.idTokenClaims;
            const user = {
                id: idTokenClaims.oid || userAccount.uniqueId || 'unknown',
                name: idTokenClaims.name,
                email: idTokenClaims.preferred_username || effectiveLoginHint || '',
                upn: idTokenClaims.upn,
                tenantId: idTokenClaims.tid,
                loginHint: effectiveLoginHint
            };
            // Update store with validated data
            store.setTokens(tokens);
            store.setUser(user);
            store.setInitialized(true);
            console.log('Authentication successful - store updated');
        }
        catch (error) {
            console.error('MSAL initialization failed:', error);
            const errorMessage = error instanceof Error ? error.message : String(error);
            store.setAuthError(`Authentication failed: ${errorMessage}`);
            throw error;
        }
        finally {
            store.setAuthenticating(false);
            this.isInitializing = false;
        }
    }
    /**
     * Get a valid access token, refreshing if necessary
     */
    async getValidToken(scopes) {
        var _a;
        debugger;
        const store = useAuthStore.getState();
        // Check if current token is valid
        if (store.isTokenValid() && !store.isTokenExpiringSoon()) {
            const currentToken = (_a = store.auth.tokens) === null || _a === void 0 ? void 0 : _a.accessToken;
            if (currentToken) {
                console.log('Using cached valid token');
                return currentToken;
            }
        }
        // Token is expired or expiring soon, refresh it
        console.log('Token expired or expiring soon, refreshing...');
        return this.refreshTokens(scopes);
    }
    /**
     * Refresh tokens using MSAL
     */
    async refreshTokens(scopes = ['user.read']) {
        var _a;
        const store = useAuthStore.getState();
        if (!this.accountManager) {
            throw new Error('MSAL not initialized - call initialize() first');
        }
        try {
            store.setAuthenticating(true);
            store.setAuthError(undefined);
            console.log('Refreshing tokens with scopes:', scopes);
            const userAccount = await this.accountManager.ssoGetUserIdentity(scopes);
            // Update tokens in store
            const tokens = {
                accessToken: userAccount.accessToken,
                expiresAt: ((_a = userAccount.expiresOn) === null || _a === void 0 ? void 0 : _a.getTime()) || (Date.now() + 3600000),
                scopes: userAccount.scopes || scopes,
                idToken: userAccount.idToken,
                tokenType: 'Bearer'
            };
            store.setTokens(tokens);
            console.log('Tokens refreshed successfully');
            return userAccount.accessToken;
        }
        catch (error) {
            console.error('Token refresh failed:', error);
            const errorMessage = error instanceof Error ? error.message : String(error);
            store.setAuthError(`Token refresh failed: ${errorMessage}`);
            // Clear authentication on refresh failure
            store.clearAuth();
            throw error;
        }
        finally {
            store.setAuthenticating(false);
        }
    }
    /**
     * Logout and clear all authentication state
     */
    async logout() {
        const store = useAuthStore.getState();
        try {
            // Clear store state
            store.clearAuth();
            // Reset MSAL instance
            this.accountManager = null;
            this.isInitializing = false;
            console.log('Logout completed');
        }
        catch (error) {
            console.error('Logout error:', error);
            // Even if logout fails, clear the store
            store.clearAuth();
            throw error;
        }
    }
    /**
     * Check if MSAL is initialized
     */
    isInitialized() {
        return this.accountManager !== null && useAuthStore.getState().auth.isInitialized;
    }
    /**
     * Get current user from store
     */
    getCurrentUser() {
        return useAuthStore.getState().auth.user;
    }
    /**
     * Check authentication status
     */
    isAuthenticated() {
        const store = useAuthStore.getState();
        return store.auth.isAuthenticated && store.isTokenValid();
    }
}
// Create singleton instance
export const msalAdapter = new MSALStoreAdapter();
// Override store methods to use MSAL adapter
const originalStore = useAuthStore.getState();
useAuthStore.setState(Object.assign(Object.assign({}, originalStore), { initializeAuth: (loginHint) => msalAdapter.initialize(loginHint), getValidToken: (scopes) => msalAdapter.getValidToken(scopes), refreshTokens: (scopes) => msalAdapter.refreshTokens(scopes), logout: () => msalAdapter.logout() }));
export default msalAdapter;
